/**
 * Portal Init API Types
 * Zod schemas for /api/users/me endpoint validation
 */

import { z } from 'zod'

// ============================================================================
// Constants
// ============================================================================

export const TENANT_CHANNELS = ['b2b', 'ecommerce'] as const
export type TenantChannel = typeof TENANT_CHANNELS[number]
export const DEFAULT_TENANT_CHANNEL: TenantChannel = 'ecommerce'

// ============================================================================
// Shared Schemas
// ============================================================================

export const roleSummarySchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  name: z.string(),
  type: z.string(),
})

export const tenantSummarySchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  tenantStatus: z.string(),
  // Channel may be null/undefined in older data - default to ecommerce
  channel: z.enum(TENANT_CHANNELS).nullable().optional().transform(v => v ?? DEFAULT_TENANT_CHANNEL),
})

export const userProfileSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  username: z.string(),
  email: z.string(),
  fullName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  tenant: tenantSummarySchema.nullable(),
  role: roleSummarySchema,
  factoryDocumentId: z.string().nullable().optional(),
  locationDocumentId: z.string().nullable().optional(),
})

export const featureFlagsSchema = z.object({
  enableBetaFeatures: z.boolean(),
  enableAnalytics: z.boolean(),
  enableCopilot: z.boolean(),
})

// ============================================================================
// Guest Schema (unauthenticated)
// ============================================================================

export const priceListSummarySchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  currency: z.string(),
}).nullable()

export const guestResponseSchema = z.object({
  type: z.literal('guest'),
  featureFlags: featureFlagsSchema,
  ecommerce: z.object({
    priceList: priceListSummarySchema,
  }),
})

// ============================================================================
// Ecommerce User Schema
// ============================================================================

export const customerGroupSummarySchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
}).nullable()

export const pricingProfileSchema = z.object({
  user: z.object({
    id: z.number(),
    email: z.string(),
    username: z.string(),
  }),
  tenant: z.object({
    id: z.number(),
    documentId: z.string(),
    name: z.string(),
    // Channel may be null/undefined in older data - default to ecommerce
    channel: z.enum(TENANT_CHANNELS).nullable().optional().transform(v => v ?? DEFAULT_TENANT_CHANNEL),
  }).nullable(),
  customerGroup: customerGroupSummarySchema,
  priceList: priceListSummarySchema,
}).nullable()

export const ecommerceResponseSchema = z.object({
  type: z.literal('ecommerce'),
  profile: userProfileSchema,
  pricingProfile: pricingProfileSchema,
  featureFlags: featureFlagsSchema,
})

// ============================================================================
// Customer User Schema
// ============================================================================

export const customerAccountSummarySchema = z.object({
  id: z.number(),
  documentId: z.string(),
  accountNumber: z.string(),
  creditLimit: z.number(),
  paymentTerms: z.string(),
  accountStatus: z.string(),
  vibanNumber: z.string().nullable().optional(),
  allowedRegions: z.array(z.object({
    id: z.number(),
    documentId: z.string(),
    code: z.string(),
    name: z.string(),
  })).nullable().optional().transform(value => value ?? []),
  allowedCities: z.array(z.object({
    id: z.number(),
    documentId: z.string(),
    code: z.string(),
    name: z.string(),
  })).nullable().optional().transform(value => value ?? []),
})

export const userSettingsSchema = z.object({
  language: z.string(),
  timezone: z.string(),
  notifications: z.object({
    email: z.boolean(),
    push: z.boolean(),
    sms: z.boolean(),
  }),
  preferences: z.record(z.string(), z.unknown()),
})

export const customerResponseSchema = z.object({
  type: z.literal('customer'),
  profile: userProfileSchema,
  customerAccount: customerAccountSummarySchema.nullable(),
  settings: userSettingsSchema,
  featureFlags: featureFlagsSchema,
})

// ============================================================================
// Admin User Schema
// ============================================================================

export const permissionSchema = z.object({
  action: z.string(),
  subject: z.string(),
  conditions: z.record(z.string(), z.unknown()).optional(),
})

export const permissionsMatrixSchema = z.object({
  canManageTenants: z.boolean(),
  canManageUsers: z.boolean(),
  canManageCustomerAccounts: z.boolean(),
  canViewAllOrders: z.boolean(),
  canApproveOrders: z.boolean(),
  canManageFleet: z.boolean(),
  canViewReports: z.boolean(),
  detailed: z.array(permissionSchema),
})

export const adminResponseSchema = z.object({
  type: z.literal('admin'),
  profile: userProfileSchema,
  permissions: permissionsMatrixSchema,
  featureFlags: featureFlagsSchema,
})

// ============================================================================
// Union Response Schema
// ============================================================================

export const meResponseSchema = z.discriminatedUnion('type', [
  guestResponseSchema,
  ecommerceResponseSchema,
  customerResponseSchema,
  adminResponseSchema,
])

// ============================================================================
// Inferred Types
// ============================================================================

export type GuestResponseDto = z.infer<typeof guestResponseSchema>
export type EcommerceResponseDto = z.infer<typeof ecommerceResponseSchema>
export type CustomerResponseDto = z.infer<typeof customerResponseSchema>
export type AdminResponseDto = z.infer<typeof adminResponseSchema>
export type MeResponseDto = z.infer<typeof meResponseSchema>

// Legacy types for backwards compatibility
export type CustomerInitDataDto = CustomerResponseDto
export type AdminInitDataDto = AdminResponseDto

// ============================================================================
// Endpoints
// ============================================================================

export const INIT_ENDPOINTS = {
  ME: '/api/users/me',
  // Legacy - to be removed
  INIT: '/api/init',
} as const

// ============================================================================
// Validators
// ============================================================================

export function validateMeResponse(data: unknown): MeResponseDto {
  return meResponseSchema.parse(data)
}

// Legacy validator
export function validateInitResponse(data: unknown): MeResponseDto {
  return validateMeResponse(data)
}
